import { Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid";
import { GridColumns } from "@mui/x-data-grid";
import React from "react";
import { useTranslation } from "react-i18next";
import { PositionDateQuickfilter } from "../../../containers/Quickfilters/PositionDateQuickfilter";
import { usePositionDateQuickFilter } from "../../../containers/Quickfilters/PositionDateQuickfilter/usePositionDateQuickFilter";
import QuickfiltersContainer from "../../../containers/Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import { PropsFilter } from "../../../models/api/props-filter/props-filter";
import { InterviewDTO } from "../../../models/recruiting/interviews/interview-dto";
import { renderCellExpand } from "../../../reusable-components/RenderCellExpand/RenderCellExpand";
import { FormField } from "../../../reusable-components/Table/AddEditItem/interfaces";
import { ColumnsHeaderTitle } from "../../../reusable-components/Table/styles";
import Table from "../../../reusable-components/Table/Table";
import { CandidateApiService } from "../../../services/recruiting/candidate/candidate-api.service";
import { InterviewsApiService } from "../../../services/recruiting/interviews/interviews-api-service";
import { RecruitersApiService } from "../../../services/recruiting/recruiters/recruiters-api.service";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import { FT_IT } from "../../../utils/constants/filter-tables-constants";
import { getOnlyTime, intlFormatDateShort } from "../../../utils/time-utils";

const Interviews = () => {
  const { t } = useTranslation();
  const tRP = "pages.system_settings.tables.interviews";
  const translationFields = `${tRP}.fields`;

  const isAllowedEditAddDelete = useAuthorization([
    AUTHORIZATIONS.RecruitingAccess.CanOpenPositionsAndAddCreateCandidates,
  ]);

  const { positionDateQuickFilter, setPositionDateQuickFilter } = usePositionDateQuickFilter();

  const columns: GridColumns = [
    {
      field: "interviewCode",
      valueGetter: ({ row }) => row?.interviewCode,
      headerName: t(`${translationFields}.interview_code`),
      headerAlign: "center",
      flex: 1,
      maxWidth: 70,
      align: "center",
    },
    {
      field: "dateInterview",
      renderCell: ({ row: { dateInterview } }) => (
        <Grid>
          <div>{intlFormatDateShort(dateInterview)}</div>
          <div>{getOnlyTime(dateInterview)}</div>
        </Grid>
      ),
      headerName: t(`${translationFields}.date`),
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "candidate",
      valueGetter: ({ row }) => row?.candidate?.name,
      headerName: t(`${translationFields}.candidate`),
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "interviewer",
      valueGetter: ({ row }) => row?.interviewer?.name,
      headerName: t(`${translationFields}.interviewer`),
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "interviewType",
      valueGetter: ({ row }) => row?.interviewType,
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.interview_type`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.interview_type`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      // headerName: t(`${translationFields}.interview_type`),
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "comment",
      valueGetter: ({ row }) => row?.comment,
      headerName: t(`${translationFields}.comment`),
      headerAlign: "center",
      flex: 1,
      renderCell: renderCellExpand,
    },
  ];

  const formFields: FormField[] = [
    {
      labelKey: t(`${translationFields}.interview_code`),
      field: "interviewCode",
      fieldType: "textfield",
    },
    {
      labelKey: t(`${translationFields}.date`),
      field: "dateInterview",
      fieldType: "dateTimePicker",
    },
    {
      labelKey: t(`${translationFields}.candidate`),
      field: "candidate",
      fieldType: "dropdown",
      apiService: new CandidateApiService(),
    },
    {
      labelKey: t(`${translationFields}.interviewer`),
      field: "interviewer",
      fieldType: "dropdown",
      apiService: new RecruitersApiService(),
    },
    {
      labelKey: t(`${translationFields}.interview_type`),
      field: "interviewType",
      fieldType: "textfield",
    },
    {
      labelKey: t(`${translationFields}.comment`),
      field: "comment",
      fieldType: "textarea",
    },
  ];

  const defaultItem: Partial<InterviewDTO> = {
    interviewCode: "",
    dateInterview: null,
    candidate: null,
    interviewer: null,
    interviewType: null,
    comment: "",
  };

  return (
    <>
      {isAllowedEditAddDelete && (
        <Table
          readOnly={!isAllowedEditAddDelete}
          translationRootPath={tRP}
          columns={columns}
          serviceApi={new InterviewsApiService()}
          formFields={formFields}
          defaultItem={defaultItem}
          filter={FT_IT}
          propsFilter={
            {
              fromMonthsAgo: positionDateQuickFilter.positionDate.fromMonthsAgo,
            } as PropsFilter
          }
          quickFilters={
            <QuickfiltersContainer>
              <PositionDateQuickfilter
                quickFilters={positionDateQuickFilter}
                handleChangeQuickFilters={setPositionDateQuickFilter}
              />
            </QuickfiltersContainer>
          }
        />
      )}
    </>
  );
};

export default Interviews;
